'use client';

import { useTranslation } from 'next-i18next';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@chakra-ui/react';

import debounce from 'lodash/debounce';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { APIProvider, Map, MapCameraChangedEvent, useMap } from '@vis.gl/react-google-maps';

import { User } from '@nocowanie/noc-api';

import { FeatureToggleEnum } from '@nocowanie/core/enums';

import { MapEditModeSidebar } from '@app/components/molecules/map-edit-mode-sidebar';
import { MapMarkerRenderer } from '@app/components/molecules/map-marker-renderer';
import { MapPopover } from '@app/components/molecules/map-popover';
import { MAP_DEFAULTS } from '@app/consts';
import { MarkerPriorityEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import {
    areBoundsInside,
    areTilesEqual,
    areTilesInside,
    BoundingBox,
} from '@app/helpers/map-tiling';
import { getBoundsZoomLevel, handleOverlappingMarkers } from '@app/helpers/map-utils';
import { runWhenIdle } from '@app/helpers/ssr.helpers';
import { useMapAccommodationClusters } from '@app/hooks/useMapAccommodationClusters';
import { useMapPopoverPosition } from '@app/hooks/useMapPopoverPosition';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { useMapTiles } from '@app/hooks/useMapTiles';
import { useUserProfile } from '@app/hooks/useUserProfile';
import { LocationDetailsModel } from '@app/models';
import {
    AccommodationClustersInput,
    ClusterOrMarkerType,
    ClusterType,
    SelectedAccommodationType,
} from '@app/models/map-marker.model';
import { ReservationTypeEnum } from '@app/models/reservation-type.model';

export const MapView = () => {
    return (
        <APIProvider apiKey={environment.GOOGLE_MAPS_API_KEY} libraries={['marker', 'geometry']}>
            <MapViewInner />
        </APIProvider>
    );
};

const MapViewInner = () => {
    const map = useMap();
    const {
        filters,
        searchCriteria,
        isMobileRes,
        isLoadingAccommodations,
        isMapLoaded,
        setIsMapLoaded,
        mapAccommodations,
        locationDetails,
        setLocationDetails,
        fetchAccommodations,
        setIsClustering,
        accommodationsError,
    } = useMapSearch();

    const { t } = useTranslation('common', {
        keyPrefix: 'map',
    });

    const { ZOOM, MIN_ZOOM, MAX_ZOOM, MANUAL_ZOOM_THRESHOLD } = MAP_DEFAULTS;
    const USE_MAP_TILING = useFeatureIsOn(FeatureToggleEnum.USE_MAP_TILING as string);
    const USE_MAP_TILING_V2 = useFeatureIsOn(FeatureToggleEnum.USE_MAP_TILING_V2 as string);

    const { user } = useUserProfile();
    const isBokUser = user?.type === User.type.BOK;

    const isOneAlgorithmMode = !!(
        USE_MAP_TILING &&
        searchCriteria.debug &&
        searchCriteria.oneAlgorithm
    );

    const [camera, setCamera] = useState<{
        center: google.maps.LatLngLiteral | null;
        zoom: number | null;
        bounds: google.maps.LatLngBoundsLiteral | null;
    }>({
        center: null,
        zoom: null,
        bounds: null,
    });

    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedMarker, setSelectedMarker] =
        useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    const [selectedAccommodation, setSelectedAccommodation] =
        useState<SelectedAccommodationType | null>(null);

    const [popoverShown, setPopoverShown] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<google.maps.LatLngLiteral | null>(null);
    const [isPopoverTopAnchor, setIsPopoverTopAnchor] = useState(false);
    const [shouldFetchAccommodations, setShouldFetchAccommodations] = useState(true);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [isClusterZoomTriggered, setIsClusterZoomTriggered] = useState(false);
    const [lastFetchedBounds, setLastFetchedBounds] =
        useState<google.maps.LatLngBoundsLiteral | null>(null);
    const [showFetchDataBtn, setShowFetchDataBtn] = useState(false);
    const [isTilesLoaded, setIsTilesLoaded] = useState(false);
    const [clusteredMarkers, setClusteredMarkers] = useState<ClusterOrMarkerType[] | undefined>();
    const { getTilesForBounds, geoTilesBounds } = useMapTiles(map, USE_MAP_TILING);
    const [gridTiles, setGridTiles] = useState<BoundingBox[]>([]);
    const prevGridTilesRef = useRef<BoundingBox[]>([]);

    const debouncedUpdateLocation = useMemo(
        () => debounce(setLocationDetails, 100),
        [setLocationDetails],
    );

    useEffect(() => {
        if (!map) return;

        // Run once
        const listener = map.addListener('tilesloaded', () => {
            setIsTilesLoaded(true);
            listener.remove();
        });

        return () => listener.remove();
    }, [map, setIsMapLoaded]);

    const debouncedClusterUpdate = useMemo(() => {
        return debounce(
            (
                newClusterData: AccommodationClustersInput,
                cb: (args: AccommodationClustersInput) => void,
            ) => {
                cb(newClusterData);
            },
            400,
        );
    }, []);

    const [clusterData, setClusterData] = useState<AccommodationClustersInput>({
        points: mapAccommodations,
        bounds: camera.bounds,
        zoom: camera.zoom ?? ZOOM,
    });

    const clusteredMarkersRaw = useMapAccommodationClusters(clusterData);

    useEffect(() => {
        if (!clusteredMarkersRaw) {
            return;
        }

        const clusteredMarkers = handleOverlappingMarkers(clusteredMarkersRaw);
        setClusteredMarkers(clusteredMarkers);
    }, [clusteredMarkersRaw]);

    const boundsPadding = 8;
    const markerCount = clusteredMarkers?.length ?? 0;
    const zIndexPopover = markerCount * MarkerPriorityEnum.Popover;

    useEffect(() => {
        if ((clusteredMarkers || accommodationsError) && isTilesLoaded && !isMapLoaded) {
            runWhenIdle(() => setIsMapLoaded(true));
        }
    }, [clusteredMarkers, accommodationsError, isTilesLoaded, isMapLoaded, setIsMapLoaded]);

    const updateClusterData = useCallback(() => {
        const newClusterData: AccommodationClustersInput = {
            points: mapAccommodations,
            bounds: camera.bounds,
            zoom: camera.zoom ?? ZOOM,
        };

        if (hasUserInteracted) {
            debouncedClusterUpdate(newClusterData, data => {
                setClusterData(data);
            });
        } else {
            setClusterData(newClusterData);
        }
    }, [
        mapAccommodations,
        hasUserInteracted,
        debouncedClusterUpdate,
        camera.bounds,
        camera.zoom,
        ZOOM,
    ]);

    // Different hook dependencies for backward compatibility – until feature toggle (USE_MAP_TILING_V2) is removed
    useEffect(() => {
        if (!USE_MAP_TILING_V2 || isClusterZoomTriggered || !mapAccommodations) return;

        updateClusterData();
    }, [mapAccommodations, isClusterZoomTriggered, USE_MAP_TILING_V2]);

    useEffect(() => {
        if (USE_MAP_TILING_V2 || isClusterZoomTriggered || !mapAccommodations || !camera.bounds)
            return;

        updateClusterData();
    }, [
        mapAccommodations,
        camera.bounds,
        camera.zoom,
        debouncedClusterUpdate,
        hasUserInteracted,
        isClusterZoomTriggered,
        ZOOM,
        USE_MAP_TILING_V2,
    ]);

    useEffect(() => {
        return () => {
            debouncedClusterUpdate.cancel();
        };
    }, [debouncedClusterUpdate]);

    const handleIdle = useCallback(() => {
        if (isClusterZoomTriggered && map && mapAccommodations) {
            setIsClustering(true);
            const newClusterData: AccommodationClustersInput = {
                points: mapAccommodations,
                bounds: map.getBounds()?.toJSON() ?? null,
                zoom: map.getZoom() ?? ZOOM,
            };

            requestAnimationFrame(() => {
                setClusterData(newClusterData);
                setIsClusterZoomTriggered(false);
                setIsClustering(false);
            });
        }
    }, [isClusterZoomTriggered, map, mapAccommodations, ZOOM]);

    const handleBoundsChanged = useCallback(
        (event: MapCameraChangedEvent) => {
            const { bounds, center, zoom } = event.detail;
            setHasUserInteracted(true);

            // temporary debug logs for testing purposes
            if (typeof window !== 'undefined' && localStorage.getItem('debug_user') === '1') {
                console.log('[MapView] zoom', zoom);
            }
            setCamera({ center, zoom, bounds });
            if (!locationDetails) return;

            if (lastFetchedBounds && !USE_MAP_TILING_V2) {
                const boundsChanged = !areBoundsInside(bounds, lastFetchedBounds);

                setShowFetchDataBtn(boundsChanged);
            }

            let tilesBounds: LocationDetailsModel['details']['location']['tilesBounds'];
            if (USE_MAP_TILING) {
                const tiles = getTilesForBounds(bounds, zoom);
                setGridTiles(tiles);

                if (isOneAlgorithmMode) {
                    tilesBounds = geoTilesBounds;
                } else {
                    tilesBounds = tiles.map(tile => ({
                        maxLat: tile.latMax,
                        maxLon: tile.lonMax,
                        minLat: tile.latMin,
                        minLon: tile.lonMin,
                    }));
                }
            }

            const updatedLocationDetails: LocationDetailsModel = {
                ...locationDetails,
                details: {
                    ...locationDetails.details,
                    location: {
                        ...locationDetails.details.location,
                        bounds: {
                            maxLat: bounds.north,
                            maxLon: bounds.east,
                            minLat: bounds.south,
                            minLon: bounds.west,
                        },
                        tilesBounds,
                    },
                },
            };

            if (shouldFetchAccommodations) {
                // Immediately update location details if we need to fetch accommodations based on bounds
                setLocationDetails(updatedLocationDetails);
            } else {
                debouncedUpdateLocation(updatedLocationDetails);
            }
        },
        [
            locationDetails,
            shouldFetchAccommodations,
            debouncedUpdateLocation,
            setLocationDetails,
            lastFetchedBounds,
            getTilesForBounds,
            USE_MAP_TILING,
            USE_MAP_TILING_V2,
            isOneAlgorithmMode,
            geoTilesBounds,
        ],
    );

    const handleSearchInThisArea = () => {
        setShowFetchDataBtn(false);
        resetMarkerState();

        if (camera.bounds) {
            setLastFetchedBounds(camera.bounds);
        }

        setTimeout(() => {
            fetchAccommodations({ skipSingleAccommodation: true });
        }, 0);
    };

    const onMouseEnter = useCallback((id: string | null) => setHoveredId(id), []);
    const onMouseLeave = useCallback(() => setHoveredId(null), []);

    const onAccommodationMarkerClick = useCallback(
        (
            id: string | null,
            marker?: google.maps.marker.AdvancedMarkerElement,
            rate?: number,
            baseRate?: number,
            currency?: string,
            reservationType?: ReservationTypeEnum,
            cheapestRateCheckIn?: string,
            cheapestRateCheckOut?: string,
            isUnavailable?: boolean,
        ) => {
            if (id === selectedId && popoverShown) {
                return;
            }

            requestAnimationFrame(() => {
                if (marker) {
                    setSelectedMarker(marker);
                    setSelectedAccommodation(
                        id
                            ? {
                                  id,
                                  rate,
                                  baseRate,
                                  currency,
                                  reservationType,
                                  cheapestRateCheckIn,
                                  cheapestRateCheckOut,
                                  isUnavailable,
                              }
                            : null,
                    );
                }

                if (!id) return;

                setSelectedId(id);

                setPopoverShown(true);
            });
        },
        [selectedId, popoverShown],
    );

    const onClusterMarkerClick = useCallback(
        (cluster: ClusterType) => {
            if (!map || !cluster.points.length) {
                return;
            }

            setIsClusterZoomTriggered(true);

            const currentZoom = map.getZoom() ?? ZOOM;
            const bounds = new google.maps.LatLngBounds();

            cluster.points.forEach(point => {
                bounds.extend(point.position);
            });

            const mapDiv = map.getDiv();
            const mapDim = {
                width: mapDiv.offsetWidth,
                height: mapDiv.offsetHeight,
            };

            const targetZoom = getBoundsZoomLevel(bounds, mapDim, boundsPadding);
            const zoomStep = targetZoom - currentZoom;
            const forceManualZoom = currentZoom < MANUAL_ZOOM_THRESHOLD && zoomStep < 2;

            // Use fitBounds for smooth zooming when possible; otherwise - apply a manual zoom step for better control
            if (targetZoom > currentZoom && !forceManualZoom) {
                requestAnimationFrame(() => {
                    map.fitBounds(bounds, boundsPadding);
                });
            } else {
                const zoomIncrement = currentZoom >= MANUAL_ZOOM_THRESHOLD && isMobileRes ? 1 : 2;
                const newZoom = Math.min(currentZoom + zoomIncrement, MAX_ZOOM);

                requestAnimationFrame(() => {
                    map.setZoom(newZoom);
                    map.setCenter(cluster.position);
                });
            }
        },
        [map, boundsPadding, isMobileRes, MAX_ZOOM, ZOOM],
    );

    const resetMarkerState = useCallback(() => {
        setSelectedId(null);
        setSelectedMarker(null);
        setSelectedAccommodation(null);
        setPopoverShown(false);
    }, []);

    const debouncedTilesCheck = useMemo(
        () =>
            debounce(() => {
                if (isLoadingAccommodations) {
                    return;
                }

                resetMarkerState();
                fetchAccommodations({ skipSingleAccommodation: true });
                prevGridTilesRef.current = [...gridTiles];
            }, 500),
        [fetchAccommodations, gridTiles, isLoadingAccommodations, resetMarkerState],
    );

    useEffect(() => {
        if (
            !USE_MAP_TILING_V2 ||
            !map ||
            !isTilesLoaded ||
            !gridTiles ||
            !locationDetails?.details.location?.tilesBounds ||
            !hasUserInteracted
        ) {
            return;
        }

        const tilesUnchanged = areTilesEqual(prevGridTilesRef.current, gridTiles);
        if (tilesUnchanged) {
            return;
        }

        if (areTilesInside(prevGridTilesRef.current, gridTiles)) {
            updateClusterData();
            return;
        }

        debouncedTilesCheck();

        return () => {
            debouncedTilesCheck.cancel();
        };
    }, [
        locationDetails?.details.location?.tilesBounds,
        gridTiles,
        map,
        isTilesLoaded,
        debouncedTilesCheck,
        hasUserInteracted,
        USE_MAP_TILING_V2,
    ]);

    useMapPopoverPosition(
        map,
        selectedMarker,
        setPopoverPosition,
        setIsPopoverTopAnchor,
        camera.zoom,
    );

    useEffect(() => {
        if (!locationDetails?.details.location) return;
        /**
         * 1. When user searches for a new location, searchCriteria?.location and locationDetails.name is updated
         * 2. This triggers camera to center on the new coordinates
         * 3. Centering causes map viewport bounds to update
         * 4. Updated bounds are then used to fetch accommodations in the visible rectangle area
         */
        setCamera({
            center: {
                lat: locationDetails.details.location.lat,
                lng: locationDetails.details.location.lon,
            },
            zoom: locationDetails?.details?.zoom ?? ZOOM,
            bounds: null,
        });
    }, [locationDetails?.details.location.lat, locationDetails?.details.location.lon]);

    useEffect(() => {
        setShouldFetchAccommodations(true);
    }, [searchCriteria?.geoId]);

    useEffect(() => {
        setHasUserInteracted(false);
        resetMarkerState();

        if (camera.bounds) {
            setLastFetchedBounds(camera.bounds);
            setShowFetchDataBtn(false);
        }
    }, [searchCriteria, filters]);

    useEffect(() => {
        if (shouldFetchAccommodations && locationDetails?.details.location?.bounds) {
            fetchAccommodations({ isOneAlgorithmMode });
            setShouldFetchAccommodations(false);
            setShowFetchDataBtn(false);

            if (USE_MAP_TILING_V2) {
                prevGridTilesRef.current = [...gridTiles];
            }

            if (camera.bounds) {
                setLastFetchedBounds(camera.bounds);
            }
        }
    }, [locationDetails?.details.location?.bounds]);

    return (
        <Map
            mapId={environment.GOOGLE_MAPS_MAP_ID}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            center={camera.center}
            zoom={camera.zoom}
            onBoundsChanged={handleBoundsChanged}
            onIdle={handleIdle}
            clickableIcons={false}
            disableDefaultUI
            zoomControl={!isMobileRes}
            onClick={resetMarkerState}
            gestureHandling="greedy"
            style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                touchAction: 'none',
                opacity: isMapLoaded ? 1 : 0,
                visibility: isMapLoaded ? 'visible' : 'hidden',
                transition: 'opacity 0.5s ease, visibility 0.5s ease',
                zIndex: 'auto',
            }}
        >
            {clusteredMarkers?.map((marker, index) => (
                <MapMarkerRenderer
                    key={marker.id}
                    marker={marker}
                    index={index}
                    totalItems={clusteredMarkers.length}
                    hoveredId={hoveredId}
                    selectedId={selectedId}
                    onAccommodationMarkerClick={onAccommodationMarkerClick}
                    onClusterMarkerClick={onClusterMarkerClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                />
            ))}

            {popoverShown && popoverPosition && selectedAccommodation ? (
                <MapPopover
                    key={selectedId}
                    position={popoverPosition}
                    onClose={() =>
                        requestAnimationFrame(() => {
                            resetMarkerState();
                        })
                    }
                    zIndex={zIndexPopover}
                    popoverData={selectedAccommodation}
                    isAnchorTop={isPopoverTopAnchor}
                />
            ) : null}

            {showFetchDataBtn ? (
                <Button
                    position="absolute"
                    top={isMobileRes ? 90 : 175}
                    left="50%"
                    transform="translateX(-50%)"
                    size={isMobileRes ? 'sm' : 'md'}
                    variant={'outlineBg'}
                    boxShadow={'2xl'}
                    onClick={handleSearchInThisArea}
                    disabled={isLoadingAccommodations}
                >
                    {t('searchInThisArea')}
                </Button>
            ) : null}

            {isBokUser && isOneAlgorithmMode && !isMobileRes ? (
                <MapEditModeSidebar map={map} />
            ) : null}
        </Map>
    );
};
